/**
 * Belegungslogik der beiden `wasteCode`-Felder (D-19): welcher AVV-Schluessel ist bereits in
 * welcher Zeile vergeben, und woran erkennt eine memoisierte Zeile, dass sich diese Belegung
 * seit ihrem letzten Render geaendert hat.
 *
 * Reines Datenmodul ohne React-Import -- die Verdrahtung mit `BrennstoffeSection` (State,
 * Comparator-Erweiterung) kommt in Task 3 dieses Plans.
 */

import type {
  AlternativeFuelEntry,
  EnvironmentalDataFormData,
} from '../../../types/reports.js';
import type { WasteCodeUsage } from '../../form/avvCatalog.js';

/**
 * Abschnittsnamen fuer die Fundstellentexte -- wortgleich mit `SECTION_LABELS` im
 * Backend-Validator (`AlternativeFuelWasteCodesValidValidator`, Plan 26-02) und mit den
 * `FormSubsection`-Titeln ohne Klammerzusatz. Feldfehler (Backend) und Dropdown-Sperrhinweis
 * (Frontend) nennen damit dieselbe Fundstelle. Seit Phase 34 nur noch dieser eine Eintrag --
 * der Sonstige-Block ist mit dem Umbau der Sekundaerbrennstoffe auf freie Zeilen entfallen.
 */
export const WASTE_CODE_SECTION_LABELS = {
  alternativeFuels: 'Sekundärbrennstoffe',
} as const;

type WasteCodeRoot = keyof typeof WASTE_CODE_SECTION_LABELS;

/** Menschenlesbare Fundstelle, 1-basierte Zeilennummer -- wie in der Backend-Meldung (D-25). */
export function wasteCodeUsageLabel(root: WasteCodeRoot, index: number): string {
  return `${WASTE_CODE_SECTION_LABELS[root]}, Zeile ${index + 1}`;
}

type UsedWasteCodesInput = Pick<EnvironmentalDataFormData, 'alternativeFuels'>;

function collectFromArray(
  root: WasteCodeRoot,
  entries: AlternativeFuelEntry[] | null | undefined,
  used: Map<string, WasteCodeUsage>,
): void {
  entries?.forEach((entry, index) => {
    const code = entry.wasteCode?.trim();
    if (!code) {
      return;
    }
    // Erster Fund gewinnt -- der zweite bleibt der Backend-Duplikatmeldung (D-07) ueberlassen.
    if (used.has(code)) {
      return;
    }
    used.set(code, { field: `${root}.${index}.wasteCode`, label: wasteCodeUsageLabel(root, index) });
  });
}

/**
 * Baut die Belegungs-Map aus dem aktuellen Formularstand: Code -> Fundstelle. Laeuft
 * `alternativeFuels` durch, ueberspringt Eintraege ohne (bzw. nach `trim()` leeren) `wasteCode`
 * und behaelt bei einer Kollision die ERSTE Fundstelle -- die Duplikatpruefung bleibt auch nach
 * dem Wegfall des Sonstige-Blocks bestehen, jetzt ausschliesslich INNERHALB dieses einen Arrays.
 */
export function collectUsedWasteCodes(values: UsedWasteCodesInput): Map<string, WasteCodeUsage> {
  const used = new Map<string, WasteCodeUsage>();
  collectFromArray('alternativeFuels', values.alternativeFuels, used);
  return used;
}

/**
 * Stabile Signatur einer Belegungs-Map fuer Re-Render-Vergleiche (Pitfall 2): der Besitzer
 * gehoert bewusst mit in die Signatur, nicht nur der Code -- raeumt Zeile 3 ihren Code und
 * waehlt Zeile 5 denselben, bleibt die Schluesselmenge gleich, die Sperrung wandert aber. Eine
 * Signatur nur aus den Codes wuerde das noetige Re-Render verschlucken.
 */
export function usedWasteCodesSignature(used: ReadonlyMap<string, WasteCodeUsage>): string {
  return [...used.entries()]
    .map(([code, usage]) => `${code}>${usage.field}`)
    .sort()
    .join('|');
}
